import { Table } from 'antd';

import SectionNavigation from '../SectionNavigation';

const TechnicalDocsTable = ({ technicalDocs, showNavigation = true, mode = 'full', productType }) => {
  // 根据产品类型和文档类型获取下载按钮文本
  const getDownloadText = (docType) => {
    // applicationNotes 类型不显示下载按钮
    if (docType === 'applicationNotes') {
      return null;
    }

    // GD32E231 产品的 datasheets 和 errata
    if (productType === 'GD32E231' && (docType === 'datasheets' || docType === 'errata')) {
      return '下载 (Rev.1.0)';
    }

    // XILINX-PYNQ-Z2 产品的 datasheets 和 errata
    if (productType === 'XILINXPYNQZ2' && (docType === 'datasheets' || docType === 'errata')) {
      return '下载英文版本 (Rev.M)';
    }

    // 默认文本
    return '下载完整文档 (Rev.M)';
  };
  // 技术文档的列配置（用于 FeaturesTab 中的简化版本）
  const simpleTechDocsColumns = [
    {
      title: '文档类型',
      dataIndex: 'type',
      key: 'type',
      width: 293,
      align: 'center',
    },
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title',
      width: 610,
      align: 'center',
      render: (text, record) => (
        <a
          href={record.url}
          download
          className="text-[#0093e6] hover:text-[#0077c2]"
          target="_blank"
          rel="noopener noreferrer"
          data-spm="bt"
        >
          {text}
        </a>
      ),
    },
    {
      title: '日期',
      dataIndex: 'date',
      key: 'date',
      width: 293,
      align: 'center',
    },
  ];

  // 设计与开发工具的列配置（用于 FeaturesTab 中的简化版本）
  const simpleDevToolsColumns = [
    {
      title: '评估模块和开发板',
      dataIndex: 'type',
      key: 'type',
      width: 293,
      align: 'center',
    },
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title',
      width: 610,
      align: 'center',
      render: (text, record) => (
        <a
          href={record.url}
          download
          className="text-[#0093e6] hover:text-[#0077c2]"
          target="_blank"
          rel="noopener noreferrer"
          data-spm="bt"
        >
          {text}
        </a>
      ),
    },
    {
      title: '器件型号',
      dataIndex: 'deviceModel',
      key: 'deviceModel',
      width: 293,
      align: 'center',
    },
  ];

  // 技术文档的列配置（完整版本，用于独立的技术文档 Tab）
  const techDocsColumns = [
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title',
      onHeaderCell: () => ({
        style: { textAlign: 'center' },
      }),
      render: (text, record, docType) => {
        // 数据表和勘误表显示下载图标，应用手册不显示
        const showIcon = docType === 'datasheets' || docType === 'errata';

        return (
          <a
            href={record.downloadUrl}
            className="text-[#0093e6] hover:text-[#0077c2] inline-flex items-center gap-2"
            target="_blank"
            rel="noopener noreferrer"
            data-spm="mdbt"
          >
            {showIcon && (
              <i
                className="inline-block w-[14px] h-[14px] bg-no-repeat"
                style={{
                  backgroundImage:
                    'url(//static.szlcsc.com/ecp/assets/web/page/www/xinpinGD32E231/images/index_icon_2.png)',
                  backgroundPosition: '0px -18px',
                }}
              ></i>
            )}
            <span>{text}</span>
          </a>
        );
      },
    },
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      width: 120,
      align: 'center',
    },
    {
      title: '大小 (KB)',
      dataIndex: 'size',
      key: 'size',
      width: 120,
      align: 'center',
    },
    {
      title: '日期',
      dataIndex: 'date',
      key: 'date',
      width: 120,
      align: 'center',
    },
    {
      title: '下载最新文档',
      key: 'download',
      width: 200,
      align: 'center',
      render: (_, record, docType) => {
        const downloadText = getDownloadText(docType);
        // 如果返回 null，则不显示下载按钮
        if (!downloadText) {
          return null;
        }
        return (
          <a
            href={record.downloadUrl}
            className="text-[#0093e6]"
            target="_blank"
            rel="noopener noreferrer"
            data-spm="xz"
          >
            {downloadText}
          </a>
        );
      },
    },
  ];

  // 工具与软件的列配置（完整版本）
  const toolsColumns = [
    {
      title: '培训内容',
      dataIndex: 'title',
      key: 'title',
      width: 400,
      onHeaderCell: () => ({
        style: { textAlign: 'center' },
      }),
      render: (text, record, docType) => {
        // 开发工具类型显示普通文本，其他类型显示链接
        if (docType === 'devTools') {
          return <span>{text}</span>;
        }
        return (
          <a
            href={record.downloadUrl}
            className="text-[#0093e6]"
            target="_blank"
            rel="noopener noreferrer"
            data-spm="mdpx"
          >
            {text}
          </a>
        );
      },
    },
    {
      title: '型号',
      dataIndex: 'size',
      key: 'size',
      width: 200,
      align: 'center',
      render: (text, record, docType) => {
        // 开发工具类型的型号显示为带链接的下载链接
        if (docType === 'devTools') {
          return (
            <a
              href={record.downloadUrl}
              className="text-[#0093e6]"
              target="_blank"
              rel="noopener noreferrer"
              data-spm="mdxh"
            >
              {text}
            </a>
          );
        }
        return <span>{text}</span>;
      },
    },
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      width: 150,
      align: 'center',
    },
  ];

  // 根据文档类型和模式获取对应的列配置
  const getColumns = (docType) => {
    // 简化模式：用于 FeaturesTab 中的"适合您的设计资源"部分
    if (mode === 'simple') {
      if (docType === 'technicalDocs') {
        return simpleTechDocsColumns;
      }
      if (docType === 'developmentTools') {
        return simpleDevToolsColumns;
      }
    }

    // 完整模式：用于独立的技术文档和工具与软件 Tab
    const toolsTypes = ['designKits', 'software', 'devTools'];
    return toolsTypes.includes(docType) ? toolsColumns : techDocsColumns;
  };

  // 获取文档类型标题和数量
  const getDocTypeInfo = (type) => {
    const data = technicalDocs?.[type] || [];
    const titles = {
      // 技术文档
      datasheets: '数据表',
      errata: '勘误表',
      applicationNotes: '应用手册',
      // 工具与软件
      designKits: '设计套件与评估版',
      software: '软件',
      devTools: '开发工具',
    };
    return {
      title: titles[type] || type,
      count: data.length,
    };
  };

  if (!technicalDocs) {
    return (
      <div className="py-10 px-5 text-center bg-gray-50 rounded-lg my-5">
        <h3 className="text-gray-600 mb-2">技术文档</h3>
        <p className="text-gray-400 text-sm">技术文档内容将在后续版本中完善</p>
      </div>
    );
  }

  // 简化模式：直接渲染单个表格，不显示导航
  if (mode === 'simple') {
    const docType = Object.keys(technicalDocs)[0];
    const data = technicalDocs[docType] || [];
    const columns = getColumns(docType);

    if (data.length === 0) return null;

    return (
      <Table
        bordered
        columns={columns}
        dataSource={data}
        pagination={false}
        size="middle"
        className="technical-docs-table"
        rowClassName="hover:bg-gray-50 text-xs"
      />
    );
  }

  // 完整模式：显示导航和所有文档类型
  // 导航项配置
  const navigationItems = Object.keys(technicalDocs).map((docType) => {
    const info = getDocTypeInfo(docType);
    return {
      label: info.title,
      targetId: `#${docType}`,
    };
  });

  // 渲染每个文档类型的表格
  const renderDocumentSection = (docType) => {
    const info = getDocTypeInfo(docType);
    const data = technicalDocs[docType] || [];
    const baseColumns = getColumns(docType);

    // 为所有类型的列添加 docType 参数，并动态修改列标题
    const columns = baseColumns.map((col) => {
      // 针对 AFM201T 产品的开发工具模块，将"型号"列标题改为"下载链接"
      if (col.key === 'size' && productType === 'AFM201T' && docType === 'devTools') {
        return {
          ...col,
          title: '下载链接',
          render: col.render ? (text, record) => col.render(text, record, docType) : undefined,
        };
      }

      if (col.render) {
        return {
          ...col,
          render: (text, record) => col.render(text, record, docType),
        };
      }
      return col;
    });

    if (data.length === 0) return null;

    return (
      <div key={docType} id={docType} className="mb-8">
        <h2 className="text-base mb-[10px] font-bold">
          {info.title}({info.count})
        </h2>
        <Table
          bordered
          columns={columns}
          dataSource={data}
          pagination={false}
          size="middle"
          className="technical-docs-table mb-6"
          rowClassName="hover:bg-gray-50 text-xs"
        />
      </div>
    );
  };

  return (
    <div className="block">
      {/* 文档类型导航 */}
      {showNavigation && <SectionNavigation items={navigationItems} />}
      {/* 所有文档类型的表格 */}
      {Object.keys(technicalDocs).map((docType) => renderDocumentSection(docType))}
    </div>
  );
};

export default TechnicalDocsTable;
